import {
  CircularProgress,
  InputAdornment,
  StandardTextFieldProps,
  TextField,
} from '@material-ui/core';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';
import { forwardRef } from 'react';
import { Geocoding } from 'shared/geo/GeoHelpers';
import {
  useGeoFieldProps,
  UseGeoFieldPropsOptions,
} from 'shared/geo/MapboxGeoField';

interface GeoFieldCommonProps extends Omit<
  AutocompleteProps<Geocoding, true, true, false>,
  'renderInput' | 'defaultValue' | 'value'
> {
  TextFieldProps?: StandardTextFieldProps;
}

interface GeoFieldSingleProps
  extends
    Omit<GeoFieldCommonProps, 'options' | 'onChange' | 'getOptionLabel'>,
    UseGeoFieldPropsOptions {
  value: Geocoding | undefined;
  defaultValue?: Geocoding;
  onChange?: (value: Geocoding | null) => void;
  TextFieldProps?: StandardTextFieldProps;
}

export const GeoField = forwardRef(
  (
    {
      onChange,
      TextFieldProps,
      value,
      types,
      formatOptionLabel,
      ...props
    }: GeoFieldSingleProps,
    ref,
  ) => {
    const autocompleteProps = useGeoFieldProps({ types, formatOptionLabel });
    return (
      <Autocomplete
        {...props}
        {...autocompleteProps}
        ref={ref}
        value={value}
        multiple={false}
        onChange={(_, selectedValue) => {
          onChange?.(selectedValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...TextFieldProps}
            InputProps={{
              ...params.InputProps,
              ...TextFieldProps?.InputProps,
              endAdornment: autocompleteProps.loading ? (
                <InputAdornment position="end">
                  <CircularProgress color="inherit" size={20} />
                </InputAdornment>
              ) : (
                params.InputProps.endAdornment
              ),
            }}
          />
        )}
      />
    );
  },
);

if (process.env.NODE_ENV !== 'production') {
  GeoField.displayName = 'GeoField';
}
